import murmurhash from 'murmurhash';

import { webConfig } from '@gitbook/env';
import { filterOutNullable } from '@gitbook/util-ts';

import { parseStorageDownloadURL } from './buckets';

/**
 * We only support caching for the main bucket and the GBN bucket.
 */
const ALLOWED_BUCKETS = [
    webConfig.firebase.storageBucket,
    webConfig.storage.legacyFilesPreviousBucket,
    webConfig.storage.legacyFilesBucket,
].filter(filterOutNullable);

let globalCacheHost: string | undefined = webConfig.storage.cdnHost;

/**
 * Define the global cache host to use for storage file.
 */
export function setStorageCacheHost(host: string | undefined) {
    globalCacheHost = host || webConfig.storage.cdnHost;
}

/**
 * Use a public content hostname as a storage cache host.
 * This method is called for public content to sandbox each public content.
 */
export function setPublicContentStorageCacheHost(originalHost: string) {
    setStorageCacheHost(getContentStorageCacheHost(originalHost));
}

/**
 * Compute a storage cache host for a specific content.
 * It is used to sandbox each content and avoid spam/malicious content from impacting other domains.
 */
export function getContentStorageCacheHost(contentId: string): string | undefined {
    return webConfig.storage.filesDomainSuffix
        ? `${murmurhash(contentId)}-${webConfig.storage.filesDomainSuffix}/~/files`
        : undefined;
}

/**
 * Transform a downloadURL from a storage bucket into a cached URL.
 */
export function getStorageCachedURL(downloadURL: string, cacheHostToUse?: string): string;
export function getStorageCachedURL(
    downloadURL: string | undefined | null,
    cacheHost: string | undefined = globalCacheHost
): string | undefined | null {
    if (!cacheHost || !downloadURL) {
        return downloadURL;
    }

    const original = new URL(downloadURL);

    try {
        const { bucket, path } = parseStorageDownloadURL(downloadURL);

        if (!ALLOWED_BUCKETS.includes(bucket)) {
            return downloadURL;
        }

        return `https://${cacheHost}/v0/b/${bucket}/o/${encodeURIComponent(path)}${
            original.search
        }`;
    } catch (_err) {
        return downloadURL;
    }
}
